import React from "react";
import { Helmet } from "react-helmet";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, TrendingUp, Users, Heart, ArrowRight, Mail } from "lucide-react";

const CaseStudies = () => {
  const [email, setEmail] = React.useState("");

  const handleNotifyMe = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement email subscription
    console.log("Email subscription:", email);
    setEmail("");
  };

  const previewCases = [
    {
      title: "95% Reduction in Patient Fall Incidents",
      description: "How Memorial Healthcare achieved remarkable safety improvements with AI-powered monitoring",
      icon: Heart,
      metric: "95% ↓",
      timeframe: "6 months"
    },
    {
      title: "40% Faster Cognitive Assessments",
      description: "Streamlining mental health evaluations while improving accuracy and patient experience",
      icon: TrendingUp,
      metric: "40% ↑",
      timeframe: "3 months"
    },
    {
      title: "60% Improvement in Early Detection",
      description: "Identifying at-risk patients before critical events through continuous monitoring",
      icon: Users,
      metric: "60% ↑",
      timeframe: "12 months"
    }
  ];

  return (
    <PageContainer>
      <Helmet>
        <title>Case Studies - Coming Soon | Healthcare AI Solutions</title>
        <meta name="description" content="Real-world success stories and clinical outcomes from healthcare organizations using our AI-powered solutions. Case studies coming soon." />
        <link rel="canonical" href="/resources/case-studies" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center mb-6">
              <Clock className="h-8 w-8 text-primary mr-3" />
              <span className="text-primary font-semibold">Coming Soon</span>
            </div>
            
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Real Impact Stories
              <span className="block bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">
                From Healthcare Heroes
              </span>
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Discover how leading healthcare organizations are transforming patient care with our AI-powered solutions. 
              Real results, real stories, real impact on patient outcomes.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <form onSubmit={handleNotifyMe} className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="min-w-[300px]"
                  required
                />
                <Button type="submit" className="px-6">
                  <Mail className="h-4 w-4 mr-2" />
                  Notify Me
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Preview Section */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">What to Expect</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Our upcoming case studies will showcase measurable improvements in patient outcomes, 
                operational efficiency, and clinical workflow optimization.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {previewCases.map((caseStudy, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        <caseStudy.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary">{caseStudy.metric}</div>
                        <div className="text-sm text-muted-foreground">{caseStudy.timeframe}</div>
                      </div>
                    </div>
                    <CardTitle className="text-xl group-hover:text-primary transition-colors">
                      {caseStudy.title}
                    </CardTitle>
                    <CardDescription className="text-base">
                      {caseStudy.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center text-primary font-medium group-hover:translate-x-1 transition-transform">
                      Read Full Study <ArrowRight className="h-4 w-4 ml-2" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Preview */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">In-Depth Analysis</h2>
              <p className="text-lg text-muted-foreground">
                Each case study will provide comprehensive insights into implementation, outcomes, and lessons learned.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <h3 className="text-xl font-semibold">What Each Case Study Will Include:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Detailed implementation timeline and methodology</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Quantifiable outcomes and ROI measurements</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Before and after workflow comparisons</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Staff and patient satisfaction metrics</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Challenges faced and solutions implemented</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-muted/30 p-6 rounded-lg">
                <h4 className="font-semibold mb-4">Featured Healthcare Systems</h4>
                <div className="space-y-3 text-muted-foreground">
                  <div>• Leading academic medical centers</div>
                  <div>• Community hospitals and health systems</div>
                  <div>• Specialty care facilities</div>
                  <div>• Long-term care organizations</div>
                  <div>• Ambulatory surgery centers</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Be the First to Know</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Get notified when our case studies go live and discover how healthcare organizations 
              like yours are achieving breakthrough results.
            </p>
            <Button size="lg" className="px-8">
              <Mail className="h-5 w-5 mr-2" />
              Subscribe for Updates
            </Button>
          </div>
        </div>
      </section>
    </PageContainer>
  );
};

export default CaseStudies;